// src/pages/AdvertisementGuide.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";
import { useTranslation } from "react-i18next";

type Topic = {
  id: string;
  title: string;
  items: string[];
};

const AdvertisementGuide: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [activeAnchor, setActiveAnchor] = useState<string>("ag-top");

  // lock page scroll (only inner panel scrolls)
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const topics: Topic[] = useMemo(
    () => [
      {
        id: "creating-an-ad",
        title: "Creating an ad post",
        items: [
          "Start: Open Advertising and click “+ Create new campaign”.",
          "Choose a URL in the left sidebar: It should be a valid URL which navigates to your internet home page. The button in the feed is navigating to this URL.",
          "Choose button text in the left sidebar: pick the text that fits your intent (e.g., visit your lable).",
          "Upload media: select a video or image. Prefer clean, high-contrast media and avoid tiny text. You can also drop it in the upload field.",
          "After including the advertising media click on continue in the left sidebar. To cancel click on back.",
          "After clicking continue Campaign details can be added which defines the campaign name, the target location of your ad, the budget and the delivery frequency of your ad.",
          "Campaign Name: Appears in the overview of your advertising portfolio but not in the advertising post.",
          "For Target Location see Targeting Location.",
          "For ad delivery frequency see Delivery modes ",
          "For budgets see Budgets",
          "When all settings have been set click on Launch Campaign & Upload button. After clicking the ad is checked by UNYDO and will be launched shortly after.",
        ],
      },
      {
        id: "targeting",
        title: "Targeting Location",
        items: [
          "Location targeting: choose Worldwide, a continent, a country, or specific cities depending on your campaign.",
          "City targeting best practice: use cities for local businesses/events; broaden to country/continent for scale.",
          "Interest discovery: if hashtags or topics exist, add relevant ones to align with user intent.",
          "Avoid over-narrow targeting: too many filters can reduce delivery and increase CPM.",
          "Relevance rule: your landing page/product should match the targeted audience.",
        ],
      },
      {
        id: "delivery-modes",
        title: "Delivery modes",
        items: [
          "Light: slower pacing; useful for awareness campaigns or limited budgets where you want steady delivery.",
          "Balanced: default pacing; aims for stable delivery and consistent results.",
          "Frequent: more aggressive pacing; can spend faster and reach more people quickly (often higher volatility).",
          "When to use Frequent: limited-time offers, event promotion, product launch windows.",
          "When to avoid Frequent: small audiences, very narrow targeting, or when you want stable learning.",
          "Monitor frequency: if users see the same ad too often, performance can drop (fatigue).",
        ],
      },
      {
        id: "budgets",
        title: "Budgets",
        items: [
          "Daily budget: spend up to a set amount per day. Good for always-on campaigns.",
          "Total (lifetime) budget: set a total cap for the entire campaign run. Good for fixed promos.",
          "Pacing: delivery may fluctuate based on inventory and optimization signals.",
          "Start small: begin with a modest budget to test creative/targeting before scaling.",
          "Scaling: increase budgets gradually (e.g., 10–30% steps) to keep performance stable.",
          "Multiple campaigns: you can run multiple campaigns at once, but watch overlap if targeting is identical.",
        ],
      },
      {
        id: "pricing",
        title: "Pricing: CPM, estimates, and when spend is counted",
        items: [
          "CPM basics: CPM is the cost per 1,000 impressions (views). It varies by targeting, demand, and creative quality.",
          "Estimates: any reach/spend estimate is approximate—actual results depend on inventory and competition.",
          "Spend counting: spend is usually counted when impressions are delivered (not just when an ad is created).",
          "Optimization impact: better engagement and relevance can reduce CPM over time.",
          "What increases CPM: too narrow targeting, low-quality creative, policy flags, or high competition periods.",
        ],
      },
      {
        id: "moderation",
        title: "Moderation: pending review, approval, rejection reasons",
        items: [
          "Review process: ads go through automated and/or manual checks before delivery.",
          "Pending review: your ad is queued. Edits during review may restart the review cycle.",
          "Approved: your ad can start delivering once budget and schedule are active.",
          "Rejected: review feedback should indicate the main reason. Fix the issue and resubmit.",
          "Common rejection reasons: misleading claims, prohibited content, adult content, hate/harassment, violence, scam behavior, broken URL, too much text, or low-quality landing pages.",
          "Appeals: if you believe rejection is an error, use the appeal/request review flow (if available).",
        ],
      },
      {
        id: "best-practices",
        title: "Best practices: short videos, clear CTA, legible text",
        items: [
          "Keep it short: 6–15 seconds is often strong for video. Show your key message early.",
          "Strong hook: your first second matters—show the product/value immediately.",
          "Readable overlay text: use large text and high contrast. Avoid placing text too close to edges.",
          "Clear CTA: tell users exactly what to do next (visit, book, learn, download).",
          "Match landing page: the page must match the ad promise; mismatches reduce conversions and may trigger policy issues.",
          "Use native framing: vertical media can perform better on mobile surfaces.",
          "Test variants: change one thing at a time (hook, CTA, audience) to learn what works.",
        ],
      },
      {
        id: "common-failures",
        title: "Common failures: invalid URL, unsupported media, too long video",
        items: [
          "Invalid URL: ensure it starts with https:// and loads without redirects or errors.",
          "Unsupported media: convert to common formats (JPEG/PNG for images, MP4/H.264 for video).",
          "Video too long or too large: shorten duration and compress bitrate/resolution.",
          "No sound: confirm the video has an audio track and your export didn’t strip audio.",
          "Policy block: remove prohibited content, avoid sensational claims, and ensure compliant messaging.",
          "Stuck in review: if review time seems unusually long, check for required business verification or contact support.",
        ],
      },
      {
        id: "billing",
        title: "Billing overview: invoices, totals, refunds (if applicable)",
        items: [
          "Billing dashboard: see active spend, totals, and campaign history in Ads Manager.",
          "Invoices: if enabled, invoices are available per billing period or per campaign.",
          "Payment methods: ensure your payment method is valid and has sufficient funds/limits.",
          "Failed payments: campaigns may pause if a payment fails—update your payment method to resume.",
          "Refunds: refund rules depend on your billing terms and local laws. If supported, you’ll see a refund/request flow.",
          "Support: for billing issues, contact support with campaign ID, date range, and screenshots if possible.",
        ],
      },
      {
        id: "safety-compliance",
        title: "Safety & compliance: prohibited content and responsible advertising",
        items: [
          "Be truthful: no misleading claims, fake scarcity, or impersonation.",
          "Respect privacy: don’t use sensitive personal data in targeting unless explicitly allowed and consented.",
          "No hate or harassment: ads must not target protected groups or promote discrimination.",
          "Age-restricted content: avoid adult content or ensure proper restrictions if your platform supports them.",
          "Copyright: only use media you own or have rights to use (music, logos, footage).",
          "Local law: comply with applicable advertising law (disclosures, pricing, promotions, regulated products).",
        ],
      },
    ],
    []
  );

const scrollTo = (id: string) => {
  const container = document.getElementById("ag-scroll"); // ✅ FIX
  const el = document.getElementById(id);
  if (!container || !el) return;

  const top =
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop -
    12;

  container.scrollTo({ top, behavior: "smooth" });
  window.history.replaceState(null, "", `#${id}`);
};


  // deep link on load: /help/ads#topic-id
useEffect(() => {
  const hash = (location.hash || "").replace("#", "").trim();
  if (!hash) return;

  requestAnimationFrame(() => {
    scrollTo(hash);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // Active anchor tracking inside scroll container
useEffect(() => {
  const container = document.getElementById("ag-scroll");
  if (!container) return;

  const handler = () => {
    let current = "ag-top";
    const ids = ["ag-top", ...topics.map((t) => t.id)];

    for (const id of ids) {
      const el = document.getElementById(id);
      if (!el) continue;

      const elTop =
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top;

      if (elTop <= 120) current = id;
    }

    if (current !== activeAnchor) {
      setActiveAnchor(current);
    }
  };
  handler();
  container.addEventListener("scroll", handler, { passive: true });
  return () => container.removeEventListener("scroll", handler as any);
}, [topics, activeAnchor]);

  return (
    <div style={shell}>
      {/* Top bar */}
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
  <HighLogo
    src={logo}
    style={{ width: 100, display: "block", marginLeft: "-20px", marginTop: "6px" }}
    padding={0}
    onClick={() => navigate("/gallery")}
  />
</div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginRight: 70, }}>
              <div style={jumpTitle}>Advertisement Guide</div>
        </div>
      </header>

      <main style={main}>
          <div style={jumpHint}>{t("help.account.ui.jumpHint", "Jump to a topic:")}</div>

        {/* Top jump bar */}
        <section style={jumpBar}>

          <div style={jumpPillsWrap}>
            <button
              type="button"
              onClick={() => scrollTo("ag-top")}
              className="ag-link"
              style={{ ...pillBtn, ...(activeAnchor === "ag-top" ? pillActive : null) }}
            >
              Overview
            </button>

            {topics.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => scrollTo(t.id)}
                className="ag-link"
                style={{ ...pillBtn, ...(activeAnchor === t.id ? pillActive : null) }}
                title={t.title}
              >
                {t.title.split(":")[0]}
              </button>
            ))}
          </div>
        </section>

        {/* Content card */}
        <section style={contentCard}>
          <div id="ag-scroll" style={contentScroll}>
            <div id="ag-top" />

            <div style={pageIntro}>
              <h1 style={h1}>How advertising works on UNYDO</h1>
              <p style={subhead}>
                This guide is a practical overview for setting up campaigns: from creative upload and targeting
                to review, delivery, billing, and compliance.
              </p>
            </div>

            <div style={topicColumn}>
              {topics.map((t) => (
                <article key={t.id} id={t.id} style={topicCard}>
                  <div style={topicTitleRow}>
                    <button
                      type="button"
                      className="ag-link"
                      onClick={() => scrollTo(t.id)}
                      style={topicTitleBtn}
                      aria-label={`Link to ${t.title}`}
                      title={`Link to ${t.title}`}
                    >
                      {t.title}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const url = `${window.location.origin}${window.location.pathname}#${t.id}`;
                        navigator.clipboard?.writeText(url).catch(() => {});
                      }}
                      style={copyBtn}
                      title="Copy link"
                      aria-label="Copy link"
                    >
                      ⧉
                    </button>
                  </div>

                  <ul style={topicList}>
                    {t.items.map((it, idx) => (
                      <li key={idx} style={topicItem}>
                        <span style={dot}>•</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={topicFooter}>
                    <button
                      type="button"
                      className="ag-link"
                      onClick={() => scrollTo("ag-top")}
                      style={backTopBtn}
                      title="Back to top"
                    >
                      ↑ Back to top
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <style>{`
        .ag-link { transition: color 140ms ease, opacity 140ms ease, border-color 140ms ease; }
        .ag-link:hover { color: #e5e7eb !important; opacity: 1 !important; cursor: pointer; }
        .ag-link:focus-visible { outline: 2px solid rgba(163,230,53,0.6); outline-offset: 3px; border-radius: 12px; }
      `}</style>
    </div>
  );
};

export default AdvertisementGuide;

/* ----------------- styles ----------------- */

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#b0b8c0",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 25px",
  background: "rgba(2, 6, 23, 0.9)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.2)",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
};

const main: React.CSSProperties = {
  flex: 1,
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  gap: 14,
  padding: "18px 3vw 18px",
  overflow: "hidden",
  boxSizing: "border-box",
};

const jumpBar: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.85), rgba(3,7,18,1))",
  boxShadow: "0 18px 45px rgba(0,0,0,0.75)",
  padding: "25px 14px",
  marginTop: -6,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
  overflow: "hidden",
};

const jumpTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 900,
  letterSpacing: -0.02,
  marginTop: -5,
  color: "#b0b8c0",
  whiteSpace: "nowrap",   // ✅ prevents line breaks
  flexShrink: 0,          // ✅ prevents squeezing
};

const jumpHint: React.CSSProperties = {
  marginTop: 0,
  marginBottom: 0,
  fontSize: 12,
  color: "#63696f",
};

const jumpPillsWrap: React.CSSProperties = {
  flex: "1 1 auto",
  display: "flex",
  gap: 10,
  overflowX: "auto",
  paddingBottom: 2,
};

const pillBtn: React.CSSProperties = {
  flex: "0 0 auto",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(255,255,255,0.04)",
  padding: "8px 12px",
  color: "#b0b8c0",
  fontSize: 12.5,
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const pillActive: React.CSSProperties = {
  border: "1px solid rgba(163,230,53,0.35)",
  background: "rgba(163,230,53,0.10)",
  color: "#b0b8c0",
};

const contentCard: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.90), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  overflow: "hidden",
  marginTop: "45px",
};

const contentScroll: React.CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "18px 18px 45px",
  boxSizing: "border-box",
};

const pageIntro: React.CSSProperties = {
  padding: "6px 2px 14px",
  borderBottom: "1px solid rgba(148,163,184,0.14)",
};

const h1: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  letterSpacing: -0.03,
  lineHeight: 1.1,
};

const subhead: React.CSSProperties = {
  margin: "10px 0 0",
  color: "#8d9298",
  fontSize: 15,
  lineHeight: 1.55,
  maxWidth: 980,
};

const topicColumn: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  marginTop: 14,
};

const topicCard: React.CSSProperties = {
  borderRadius: 18,
  border: "1px solid rgba(148,163,184,0.18)",
  background: "rgba(255,255,255,0.04)",
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
};

const topicTitleRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const topicTitleBtn: React.CSSProperties = {
  flex: "1 1 auto",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  color: "#b0b8c0",
  fontSize: 18,
  fontWeight: 950,
  letterSpacing: -0.02,
  cursor: "pointer",
};

const copyBtn: React.CSSProperties = {
  flex: "0 0 auto",
  width: 34,
  height: 34,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  color: "#b1b8c7",
  cursor: "pointer",
};

const topicList: React.CSSProperties = {
  margin: "12px 0 0",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 9,
};

const topicItem: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  color: "#b0b8c0",
  fontSize: 14,
  lineHeight: 1.55,
};

const dot: React.CSSProperties = {
  color: "rgba(148,163,184,0.85)",
  flex: "0 0 auto",
  marginTop: 3,
};

const topicFooter: React.CSSProperties = {
  marginTop: 12,
  display: "flex",
  justifyContent: "flex-end",
};

const backTopBtn: React.CSSProperties = {
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  padding: "8px 12px",
  color: "#647282",
  fontSize: 12.5,
  fontWeight: 800,
  cursor: "pointer",
};
